import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { isSupported as isAvModerationSupported } from '../../../av-moderation/functions';
import Avatar from '../../../base/avatar/components/Avatar';
import { isIosMobileBrowser, isMobileBrowser } from '../../../base/environment/utils';
import { MEDIA_TYPE } from '../../../base/media/constants';
import { PARTICIPANT_ROLE } from '../../../base/participants/constants';
import { getLocalParticipant, getParticipantDisplayName, hasRaisedHand } from '../../../base/participants/functions';
import { isParticipantAudioMuted, isParticipantVideoMuted } from '../../../base/tracks/functions.any';
import ContextMenu from '../../../base/ui/components/web/ContextMenu';
import ContextMenuItemGroup from '../../../base/ui/components/web/ContextMenuItemGroup';
import { getBreakoutRooms, getCurrentRoomId, isInBreakoutRoom } from '../../../breakout-rooms/functions';
import { displayVerification } from '../../../e2ee/functions';
// import { setVolume } from '../../../filmstrip/actions.web';
import { isStageFilmstripAvailable } from '../../../filmstrip/functions.web';
import { getQuickActionButtonType, isForceMuted } from '../../../participants-pane/functions';
import { requestRemoteControl, stopController } from '../../../remote-control/actions.web';
import { getParticipantMenuButtonsWithNotifyClick, showOverflowDrawer } from '../../../toolbox/functions.web';
import { NOTIFY_CLICK_MODE } from '../../../toolbox/types';
import { iAmVisitor } from '../../../visitors/functions';
import { PARTICIPANT_MENU_BUTTONS as BUTTONS } from '../../constants';
import ConnectionStatusButton from './ConnectionStatusButton';
import CustomOptionButton from './CustomOptionButton';
import DemoteToVisitorButton from './DemoteToVisitorButton';
import GrantModeratorButton from './GrantModeratorButton';
import KickButton from './KickButton';
import LowerHandButton from './LowerHandButton';
import MuteButton from './MuteButton';
import MuteVideoButton from './MuteVideoButton';
import PrivateMessageMenuButton from './PrivateMessageMenuButton';
import RemoteControlButton, { REMOTE_CONTROL_MENU_STATES } from './RemoteControlButton';
import SendToRoomButton from './SendToRoomButton';
import TogglePinToStageButton from './TogglePinToStageButton';
import VerifyParticipantButton from './VerifyParticipantButton';
import { isBannedShareScreen } from '../../../sharescreen-moderation/functions';
import { isBannedChat } from '../../../vtchat-moderation/functions';
import { isBannedUpload } from '../../../vtfileupload-moderation/functions';
import { isBannedRaisehand } from '../../../raisehand-moderation/functions';
import { isBannedPoll } from '../../../poll-moderation/functions';
import { isBannedChangeName } from '../../../changename-moderation/functions';
import { isBannedRecording } from '../../../recording-moderation/functions';
import { approveParticipantAudio, approveParticipantVideo, rejectParticipantAudioRoomMuted, rejectParticipantAudioRoomUnMuted, rejectParticipantVideoRoomMuted, rejectParticipantVideoRoomUnMuted } from '../../../av-moderation/actions';
import { approveParticipantShareScreen, rejectParticipantShareScreen } from '../../../sharescreen-moderation/slice';
import Switch from '../../../base/ui/components/web/Switch';
import { approveParticipantChat, rejectParticipantChat } from '../../../vtchat-moderation/slice';
import { approveParticipantUpload, rejectParticipantUpload } from '../../../vtfileupload-moderation/slice';
import { approveParticipantRaisehand, rejectParticipantRaisehand } from '../../../raisehand-moderation/slice';
import { approveParticipantPoll, rejectParticipantPoll } from '../../../poll-moderation/slice';
import { approveParticipantChangeName, rejectParticipantChangeName } from '../../../changename-moderation/slice';
import { approveParticipantRecording, rejectParticipantRecording } from '../../../recording-moderation/slice';
import TogglePinToAllButton from './TogglePinToAllButton';
import ChangeNameOther from './ChangeNameOther';
import { Menu } from '@mui/material';
import OpenMicButton from './OpenMicButton';
import OpenCameraButton from './OpenCameraButton';
const useStyles = makeStyles()(theme => {
    return {
        text: {
            color: theme.palette.text02,
            padding: '10px 16px',
            height: '40px',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            boxSizing: 'border-box'
        }
    };
});
const ParticipantContextMenu = ({ className, closeDrawer, drawerParticipant, offsetTarget, onEnter, onLeave, onSelect, participant, remoteControlState, thumbnailMenu, noIcon, anchorEl, open, handleClose, useMuiMenu }) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { classes: styles, cx } = useStyles();
    const _state = useSelector((state) => state);
    const [permissionSectionsActive, setPermissionSectionsActive] = useState({
        meetingInteraction: true,
        messages: true,
        others: true
    });
    const localParticipant = useSelector(getLocalParticipant);
    const _isModerator = Boolean(localParticipant?.role === PARTICIPANT_ROLE.MODERATOR);
    const _isAudioForceMuted = useSelector((state) => isForceMuted(participant, MEDIA_TYPE.AUDIO, state));
    const _isVideoForceMuted = useSelector(state => isForceMuted(participant, MEDIA_TYPE.VIDEO, state));
    const _isAudioMuted = useSelector((state) => isParticipantAudioMuted(participant, state));
    const _isVideoMuted = useSelector((state) => isParticipantVideoMuted(participant, state));
    const _overflowDrawer = useSelector(showOverflowDrawer);
    const { remoteVideoMenu = {}, disableRemoteMute, startSilent, customParticipantMenuButtons } = useSelector((state) => state['features/base/config']);
    const visitorsMode = useSelector((state) => iAmVisitor(state));
    const visitorsSupported = useSelector((state) => state['features/visitors'].supported);
    const { disableDemote, disableKick, disableGrantModerator, disablePrivateChat } = remoteVideoMenu;
    const { participantsVolume } = useSelector((state) => state['features/filmstrip']);
    const _volume = (participant?.local ?? true ? undefined
        : participant?.id ? participantsVolume[participant?.id] : undefined) ?? 1;
    const isBreakoutRoom = useSelector(isInBreakoutRoom);
    const isModerationSupported = useSelector((state) => isAvModerationSupported()(state));
    const raisedHands = hasRaisedHand(participant);
    const stageFilmstrip = useSelector(isStageFilmstripAvailable);
    const shouldDisplayVerification = useSelector((state) => displayVerification(state, participant?.id));
    const buttonsWithNotifyClick = useSelector(getParticipantMenuButtonsWithNotifyClick);
    const { audioWhitelist, videoWhitelist, audioBlacklist, videoBlacklist, roomConfiguration } = useSelector((state) => state['features/av-moderation']);
    const _currentRoomId = useSelector(getCurrentRoomId);
    const _rooms = Object.values(useSelector(getBreakoutRooms));
    // const _onVolumeChange = useCallback(value => {
    //     dispatch(setVolume(participant.id, value));
    // }, [ setVolume, dispatch ]);
    const _getCurrentParticipantId = useCallback(() => {
        const drawer = _overflowDrawer && !thumbnailMenu;
        return (drawer ? drawerParticipant?.participantID : participant?.id) ?? '';
    }, [thumbnailMenu, _overflowDrawer, drawerParticipant, participant]);
    let meetingInteractionCount = 0;
    let messagesCount = 0;
    let othersCount = 0;
    try {
        meetingInteractionCount = !_state ? 0 : (roomConfiguration?.media ? (!!videoWhitelist[_getCurrentParticipantId()] ? 1 : 0) : (!videoBlacklist[_getCurrentParticipantId()] ? 1 : 0))
            + (roomConfiguration?.audio ? (!!audioWhitelist[_getCurrentParticipantId()] ? 1 : 0) : (!audioWhitelist[_getCurrentParticipantId()] ? 1 : 0))
            + (isBannedShareScreen(participant, _state) ? 0 : 1);
        // + (isBannedWhiteboard(participant, _state) ? 0 : 1)
        messagesCount = !_state ? 0 : (isBannedChat(participant, _state) ? 0 : 1)
            + (isBannedUpload(participant, _state) ? 0 : 1)
            + (isBannedRaisehand(participant, _state) ? 0 : 1)
            + (isBannedPoll(participant, _state) ? 0 : 1);
        othersCount = !_state ? 0 : (isBannedChangeName(participant, _state) ? 0 : 1)
            + (isBannedRecording(participant, _state) ? 0 : 1);
    }
    catch (e) {
        console.log(e);
    }
    // Close context menu on mouse leave
    const onMouseLeave = (e) => {
        if (onLeave)
            onLeave(e);
        // onSelect is lowerMenu
        onSelect();
    };
    const notifyClick = useCallback((buttonKey) => {
        const notifyMode = buttonsWithNotifyClick?.get(buttonKey);
        if (!notifyMode) {
            return;
        }
        APP.API.notifyParticipantMenuButtonClicked(buttonKey, _getCurrentParticipantId(), notifyMode === NOTIFY_CLICK_MODE.PREVENT_AND_NOTIFY);
    }, [buttonsWithNotifyClick, _getCurrentParticipantId]);
    const onBreakoutRoomButtonClick = useCallback(() => {
        onSelect(true);
    }, [onSelect]);
    const isClickedFromParticipantPane = useMemo(() => !_overflowDrawer && !thumbnailMenu, [_overflowDrawer, thumbnailMenu]);
    const quickActionButtonType = useSelector((state) => getQuickActionButtonType(participant, _isAudioMuted, _isVideoMuted, state));
    const buttons = [];
    const buttons2 = [];
    const showVolumeSlider = !startSilent
        && !isIosMobileBrowser()
        // && (_overflowDrawer || thumbnailMenu)
        // && thumbnailMenu
        && participant.role !== "moderator"
        && typeof _volume === 'number'
        && !isNaN(_volume);
    const getButtonProps = useCallback((key) => {
        const notifyMode = buttonsWithNotifyClick?.get(key);
        const shouldNotifyClick = typeof notifyMode !== 'undefined';
        return {
            key,
            notifyMode,
            notifyClick: shouldNotifyClick ? () => notifyClick(key) : undefined,
            participantID: _getCurrentParticipantId(),
            noIcon
        };
    }, [_getCurrentParticipantId, buttonsWithNotifyClick, notifyClick]);
    // if (stageFilmstrip) {
    buttons.push(React.createElement(TogglePinToStageButton, { ...getButtonProps(BUTTONS.PIN_TO_STAGE) }));
    // }
    if (_isModerator) {
        // 
        buttons.push(React.createElement(TogglePinToAllButton, { ...getButtonProps(BUTTONS.PIN_TO_ALL) }));
        if (participant.role != 'moderator') {
            buttons2.push(React.createElement(ChangeNameOther, { ...getButtonProps(BUTTONS.CHANGE_NAME) }));
        }
        // if (isModerationSupported) {
        //     if (_isAudioForceMuted) {
        //         buttons.push(<AskToUnmuteButton
        //             { ...getButtonProps(BUTTONS.ASK_UNMUTE) }
        //             buttonType = { MEDIA_TYPE.AUDIO } />
        //         );
        //     }
        //     if (_isVideoForceMuted) {
        //         buttons.push(<AskToUnmuteButton
        //             { ...getButtonProps(BUTTONS.ALLOW_VIDEO) }
        //             buttonType = { MEDIA_TYPE.VIDEO } />
        //         );
        //     }
        // }
        if (participant.role != 'moderator' && !participant?.fakeParticipant && !participant?.isCasCade && !participant?.isSip) {
            buttons.push(React.createElement(OpenMicButton, { ...getButtonProps(BUTTONS.UNMUTE_MIC) }));
            buttons.push(React.createElement(OpenCameraButton, { ...getButtonProps(BUTTONS.UNMUTE_VIDEO) }));
        }
        if (participant.role != 'moderator' && !participant?.fakeParticipant) {
            // if (!(isClickedFromParticipantPane && quickActionButtonType === QUICK_ACTION_BUTTON.MUTE)) {
            !participant.isCasCade && buttons.push(React.createElement(MuteButton, { ...getButtonProps(BUTTONS.MUTE) }));
            // }
            // buttons.push(<MuteEveryoneElseButton { ...getButtonProps(BUTTONS.MUTE_OTHERS) } />);
            // if (!(isClickedFromParticipantPane && quickActionButtonType === QUICK_ACTION_BUTTON.STOP_VIDEO)) {
            !participant.isCasCade && buttons.push(React.createElement(MuteVideoButton, { ...getButtonProps(BUTTONS.MUTE_VIDEO) }));
            // }
            // buttons.push(<MuteEveryoneElsesVideoButton { ...getButtonProps(BUTTONS.MUTE_OTHERS_VIDEO) } />);
        }
        if (!disableGrantModerator && !isBreakoutRoom && !participant.isCasCade) {
            buttons2.push(React.createElement(GrantModeratorButton, { ...getButtonProps(BUTTONS.GRANT_MODERATOR) }));
        }
        if (!disableKick && participant.role != 'moderator') {
            buttons2.push(React.createElement(KickButton, { ...getButtonProps(BUTTONS.KICK), isCascade: participant.isCasCade, roomId: participant.roomId }));
        }
        if (raisedHands && !participant.isCasCade) {
            buttons2.push(React.createElement(LowerHandButton, { ...getButtonProps(BUTTONS.LOWER_PARTICIPANT_HAND) }));
        }
        if (!disableDemote && visitorsSupported && _isModerator && !participant.isCasCade) {
            buttons2.push(React.createElement(DemoteToVisitorButton, { ...getButtonProps(BUTTONS.DEMOTE) }));
        }
        if (shouldDisplayVerification && !participant.isCasCade) {
            buttons2.push(React.createElement(VerifyParticipantButton, { ...getButtonProps(BUTTONS.VERIFY) }));
        }
    }
    if (!disablePrivateChat && !visitorsMode && !participant?.fakeParticipant && !participant.isCasCade) {
        buttons.push(React.createElement(PrivateMessageMenuButton, { ...getButtonProps(BUTTONS.PRIVATE_MESSAGE) }));
    }
    if (thumbnailMenu && isMobileBrowser() && !participant.isCasCade) {
        buttons2.push(React.createElement(ConnectionStatusButton, { ...getButtonProps(BUTTONS.CONN_STATUS) }));
    }
    if (thumbnailMenu && remoteControlState && !participant.isCasCade) {
        const onRemoteControlToggle = useCallback(() => {
            if (remoteControlState === REMOTE_CONTROL_MENU_STATES.STARTED) {
                dispatch(stopController(true));
            }
            else if (remoteControlState === REMOTE_CONTROL_MENU_STATES.NOT_STARTED) {
                dispatch(requestRemoteControl(_getCurrentParticipantId()));
            }
        }, [dispatch, remoteControlState, stopController, requestRemoteControl]);
        buttons2.push(React.createElement(RemoteControlButton, { ...getButtonProps(BUTTONS.REMOTE_CONTROL), onClick: onRemoteControlToggle, remoteControlState: remoteControlState }));
    }
    if (customParticipantMenuButtons && !participant.isCasCade) {
        customParticipantMenuButtons.forEach(({ icon, id, text }) => {
            buttons2.push(React.createElement(CustomOptionButton, { icon: icon, key: id, 
                // eslint-disable-next-line react/jsx-no-bind
                onClick: () => notifyClick(id), text: text }));
        });
    }
    const breakoutRoomsButtons = [];
    if (!thumbnailMenu && _isModerator) {
        _rooms.forEach(room => {
            if (room.id !== _currentRoomId) {
                breakoutRoomsButtons.push(React.createElement(SendToRoomButton, { ...getButtonProps(BUTTONS.SEND_PARTICIPANT_TO_ROOM), key: room.id, onClick: onBreakoutRoomButtonClick, room: room }));
            }
        });
    }
    const displayName = getParticipantDisplayName(_state, _getCurrentParticipantId());
    const handleUpdateMedia = (status, id, type) => {
        if (type === 'audio') {
            if (roomConfiguration['audio'])
                dispatch(status ? rejectParticipantAudioRoomMuted(id) : approveParticipantAudio(id));
            else {
                dispatch(status ? rejectParticipantAudioRoomUnMuted(id) : approveParticipantAudio(id));
            }
        }
        if (type === 'media') {
            if (roomConfiguration['media'])
                dispatch(status ? rejectParticipantVideoRoomMuted(id) : approveParticipantVideo(id));
            else {
                dispatch(status ? rejectParticipantVideoRoomUnMuted(id) : approveParticipantVideo(id));
            }
        }
    };
    const toggleShareScreenBan = () => {
        if (isBannedShareScreen(participant, _state)) {
            if (roomConfiguration?.media ? !!videoWhitelist[_getCurrentParticipantId()] : !videoBlacklist[_getCurrentParticipantId()])
                dispatch(approveParticipantShareScreen(participant.id));
        }
        else {
            dispatch(rejectParticipantShareScreen(participant.id));
        }
    };
    const toggleChatBan = () => {
        if (isBannedChat(participant, _state)) {
            dispatch(approveParticipantChat(participant.id));
        }
        else {
            dispatch(rejectParticipantChat(participant.id));
            if (!isBannedUpload(participant, _state)) {
                dispatch(rejectParticipantUpload(participant.id));
            }
        }
    };
    const toggleUploadBan = () => {
        if (isBannedUpload(participant, _state)) {
            if (!isBannedChat(participant, _state))
                dispatch(approveParticipantUpload(participant.id));
        }
        else {
            dispatch(rejectParticipantUpload(participant.id));
        }
    };
    const toggleRaisehandBan = () => {
        if (isBannedRaisehand(participant, _state)) {
            dispatch(approveParticipantRaisehand(participant.id));
        }
        else {
            dispatch(rejectParticipantRaisehand(participant.id));
        }
    };
    const togglePollBan = () => {
        if (isBannedPoll(participant, _state)) {
            dispatch(approveParticipantPoll(participant.id));
        }
        else {
            dispatch(rejectParticipantPoll(participant.id));
        }
    };
    const toggleChangeNameBan = () => {
        if (isBannedChangeName(participant, _state)) {
            dispatch(approveParticipantChangeName(participant.id));
        }
        else {
            dispatch(rejectParticipantChangeName(participant.id));
        }
    };
    const toggleRecordingBan = () => {
        if (isBannedRecording(participant, _state)) {
            dispatch(approveParticipantRecording(participant.id));
        }
        else {
            dispatch(rejectParticipantRecording(participant.id));
        }
    };
    if (useMuiMenu) {
        return (React.createElement(Menu, { className: cx(className, 'white-context-menu', 'participant-context-menu'), anchorEl: anchorEl, open: open, onClose: () => { handleClose(); onMouseLeave(); }, MenuListProps: {
                'aria-labelledby': 'basic-button',
                onMouseLeave: () => { handleClose(); onMouseLeave(); }, // 👈 Close on mouse leave
            }, anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'right',
            }, transformOrigin: {
                vertical: 'top',
                horizontal: 'right',
            } },
            !thumbnailMenu && _overflowDrawer && drawerParticipant && (React.createElement(ContextMenuItemGroup, { actions: [
                    {
                        accessibilityLabel: drawerParticipant.displayName,
                        customIcon: React.createElement(Avatar, { participantId: drawerParticipant.participantID, size: 20 }),
                        text: drawerParticipant.displayName,
                    },
                ] })),
            buttons.length > 0 && React.createElement("div", { onClick: handleClose },
                React.createElement(ContextMenuItemGroup, null, buttons)),
            !participant?.fakeParticipant && buttons2?.length > 0 &&
                React.createElement("div", null,
                    React.createElement("div", { style: { borderTop: "1px solid #E9E9E9", margin: '0 15px' } }),
                    React.createElement("div", { onClick: handleClose },
                        React.createElement(ContextMenuItemGroup, null, buttons2))),
            _isModerator && participant.role != "moderator" && !participant?.fakeParticipant && !participant.isCasCade && (React.createElement("div", { style: { margin: "0px 15px", paddingTop: "5px", borderTop: "1px solid #E9E9E9", fontWeight: 600 } },
                t("dialog.allow"),
                " ",
                displayName,
                ":")),
            _isModerator && participant.role != "moderator" && !participant?.fakeParticipant && !participant.isCasCade &&
                React.createElement("div", { style: { marginTop: "5px" } },
                    React.createElement("div", { 
                        // className="person-permission-section-title"
                        onClick: () => setPermissionSectionsActive({
                            ...permissionSectionsActive,
                            meetingInteraction: !permissionSectionsActive.meetingInteraction,
                        }) }),
                    React.createElement("div", null,
                        React.createElement("div", { "data-id": "1", className: 'controler-item controler-item-toogle item-checkbox', style: {
                                cursor: "pointer !important",
                                marginLeft: "20px",
                            }, onClick: () => handleUpdateMedia(roomConfiguration['audio'] ? !!audioWhitelist[_getCurrentParticipantId()] : !audioBlacklist[_getCurrentParticipantId()], _getCurrentParticipantId(), 'audio') },
                            React.createElement("span", { style: { fontWeight: '500' } }, t("menuController.turnOnMicro")),
                            React.createElement(Switch, { checked: roomConfiguration['audio'] ? !!audioWhitelist[_getCurrentParticipantId()] : !audioBlacklist[_getCurrentParticipantId()], id: "micro", onChange: () => handleUpdateMedia(roomConfiguration['audio'] ? !!audioWhitelist[_getCurrentParticipantId()] : !audioBlacklist[_getCurrentParticipantId()], _getCurrentParticipantId(), 'audio') })),
                        React.createElement("div", { "data-id": "1", className: 'controler-item controler-item-toogle item-checkbox', style: {
                                cursor: "pointer !important",
                                marginLeft: "20px",
                            }, onClick: () => handleUpdateMedia(roomConfiguration['media'] ? !!videoWhitelist[_getCurrentParticipantId()] : !videoBlacklist[_getCurrentParticipantId()], _getCurrentParticipantId(), 'media') },
                            React.createElement("span", { style: { fontWeight: '500' } }, t("menuController.turnOnCamera")),
                            React.createElement(Switch, { checked: roomConfiguration['media'] ? !!videoWhitelist[_getCurrentParticipantId()] : !videoBlacklist[_getCurrentParticipantId()], id: "camera", onChange: () => handleUpdateMedia(roomConfiguration['media'] ? !!videoWhitelist[_getCurrentParticipantId()] : !videoBlacklist[_getCurrentParticipantId()], _getCurrentParticipantId(), 'media') })),
                        React.createElement("div", { "data-id": "1", className: 'controler-item controler-item-toogle item-checkbox', style: {
                                cursor: "pointer !important",
                                marginLeft: "20px",
                            }, onClick: () => toggleShareScreenBan() },
                            React.createElement("span", { style: { fontWeight: '500' } }, t('menuController.sharescreen')),
                            React.createElement(Switch, { checked: !isBannedShareScreen(participant, _state), id: "sharescreen", onChange: () => toggleShareScreenBan() })),
                        React.createElement("div", { "data-id": "1", className: 'controler-item controler-item-toogle item-checkbox', style: {
                                cursor: "pointer !important",
                                marginLeft: "20px",
                            }, onClick: () => toggleChatBan() },
                            React.createElement("span", { style: { fontWeight: '500' } }, t('menuController.privateMessage')),
                            React.createElement(Switch, { checked: !isBannedChat(participant, _state), id: "chat", onChange: () => toggleChatBan() })),
                        React.createElement("div", { "data-id": "1", className: 'controler-item controler-item-toogle item-checkbox', style: {
                                cursor: "pointer !important",
                                marginLeft: "20px",
                            }, onClick: () => toggleUploadBan() },
                            React.createElement("span", { style: { fontWeight: '500' } }, t('menuController.upload')),
                            React.createElement(Switch, { checked: !isBannedUpload(participant, _state), id: "upload", onChange: () => toggleUploadBan(), disabled: isBannedChat(participant, _state) })),
                        React.createElement("div", { "data-id": "1", className: 'controler-item controler-item-toogle item-checkbox', style: {
                                cursor: "pointer !important",
                                marginLeft: "20px",
                            }, onClick: () => toggleRaisehandBan() },
                            React.createElement("span", { style: { fontWeight: '500' } }, t('menuController.raisehand')),
                            React.createElement(Switch, { checked: !isBannedRaisehand(participant, _state), id: "raisehand", onChange: () => toggleRaisehandBan() })),
                        React.createElement("div", { "data-id": "1", className: 'controler-item controler-item-toogle item-checkbox', style: {
                                cursor: "pointer !important",
                                marginLeft: "20px",
                            }, onClick: () => togglePollBan() },
                            React.createElement("span", { style: { fontWeight: '500' } }, t('menuController.createPoll')),
                            React.createElement(Switch, { checked: !isBannedPoll(participant, _state), id: "poll", onChange: () => togglePollBan() })),
                        React.createElement("div", { "data-id": "1", className: 'controler-item controler-item-toogle item-checkbox', style: {
                                cursor: "pointer !important",
                                marginLeft: "20px",
                            }, onClick: () => toggleChangeNameBan() },
                            React.createElement("span", { style: { fontWeight: '500' } }, t('menuController.changeName')),
                            React.createElement(Switch, { checked: !isBannedChangeName(participant, _state), id: "changeName", onChange: () => toggleChangeNameBan() })),
                        React.createElement("div", { "data-id": "1", className: 'controler-item controler-item-toogle item-checkbox', style: {
                                cursor: "pointer !important",
                                marginLeft: "20px",
                            }, onClick: () => toggleRecordingBan() },
                            React.createElement("span", { style: { fontWeight: '500' } }, t('menuController.recording')),
                            React.createElement(Switch, { checked: !isBannedRecording(participant, _state), id: "recording", onChange: () => toggleRecordingBan() })))),
            breakoutRoomsButtons.length > 0 && (React.createElement(ContextMenuItemGroup, null,
                React.createElement("div", { className: styles.text }, t("breakoutRooms.actions.sendToBreakoutRoom")),
                breakoutRoomsButtons))));
    }
    return (React.createElement(ContextMenu, { className: cx(className, 'white-context-menu'), whiteUI: true, entity: participant, hidden: thumbnailMenu ? false : undefined, inDrawer: thumbnailMenu && _overflowDrawer, isDrawerOpen: Boolean(drawerParticipant), offsetTarget: offsetTarget, onClick: onSelect, onDrawerClose: thumbnailMenu ? onSelect : closeDrawer, onMouseEnter: onEnter, onMouseLeave: onMouseLeave },
        !thumbnailMenu && _overflowDrawer && drawerParticipant && (React.createElement(ContextMenuItemGroup, { actions: [
                {
                    accessibilityLabel: drawerParticipant.displayName,
                    customIcon: React.createElement(Avatar, { participantId: drawerParticipant.participantID, size: 20 }),
                    text: drawerParticipant.displayName,
                },
            ] })),
        buttons.length > 0 && React.createElement(ContextMenuItemGroup, null, buttons),
        !participant?.fakeParticipant && buttons2?.length > 0 &&
            React.createElement("div", null,
                React.createElement("div", { style: { borderTop: "1px solid #E9E9E9", margin: '0 15px' } }),
                React.createElement(ContextMenuItemGroup, null, buttons2)),
        _isModerator && participant.role != "moderator" && !participant?.fakeParticipant && !participant.isCasCade && (React.createElement("div", { style: { margin: "0px 15px", paddingTop: "5px", borderTop: "1px solid #E9E9E9", fontWeight: 600 } },
            t("dialog.allow"),
            " ",
            displayName,
            ":")),
        _isModerator && participant.role != "moderator" && !participant?.fakeParticipant && !participant.isCasCade &&
            React.createElement("div", { style: { marginTop: "5px" } },
                React.createElement("div", { 
                    // className="person-permission-section-title"
                    onClick: () => setPermissionSectionsActive({
                        ...permissionSectionsActive,
                        meetingInteraction: !permissionSectionsActive.meetingInteraction,
                    }) }),
                React.createElement("div", null,
                    React.createElement("div", { "data-id": "1", className: 'controler-item controler-item-toogle item-checkbox', style: {
                            cursor: "pointer !important",
                            marginLeft: "20px",
                        }, onClick: () => handleUpdateMedia(roomConfiguration['audio'] ? !!audioWhitelist[_getCurrentParticipantId()] : !audioBlacklist[_getCurrentParticipantId()], _getCurrentParticipantId(), 'audio') },
                        React.createElement("span", { style: { fontWeight: '500' } }, t("menuController.turnOnMicro")),
                        React.createElement(Switch, { checked: roomConfiguration['audio'] ? !!audioWhitelist[_getCurrentParticipantId()] : !audioBlacklist[_getCurrentParticipantId()], id: "micro", onChange: () => handleUpdateMedia(roomConfiguration['audio'] ? !!audioWhitelist[_getCurrentParticipantId()] : !audioBlacklist[_getCurrentParticipantId()], _getCurrentParticipantId(), 'audio') })),
                    React.createElement("div", { "data-id": "1", className: 'controler-item controler-item-toogle item-checkbox', style: {
                            cursor: "pointer !important",
                            marginLeft: "20px",
                        }, onClick: () => handleUpdateMedia(roomConfiguration['media'] ? !!videoWhitelist[_getCurrentParticipantId()] : !videoBlacklist[_getCurrentParticipantId()], _getCurrentParticipantId(), 'media') },
                        React.createElement("span", { style: { fontWeight: '500' } }, t("menuController.turnOnCamera")),
                        React.createElement(Switch, { checked: roomConfiguration['media'] ? !!videoWhitelist[_getCurrentParticipantId()] : !videoBlacklist[_getCurrentParticipantId()], id: "camera", onChange: () => handleUpdateMedia(roomConfiguration['media'] ? !!videoWhitelist[_getCurrentParticipantId()] : !videoBlacklist[_getCurrentParticipantId()], _getCurrentParticipantId(), 'media') })),
                    React.createElement("div", { "data-id": "1", className: 'controler-item controler-item-toogle item-checkbox', style: {
                            cursor: "pointer !important",
                            marginLeft: "20px",
                        }, onClick: () => toggleShareScreenBan() },
                        React.createElement("span", { style: { fontWeight: '500' } }, t('menuController.sharescreen')),
                        React.createElement(Switch, { checked: !isBannedShareScreen(participant, _state), id: "sharescreen", onChange: () => toggleShareScreenBan() })),
                    React.createElement("div", { "data-id": "1", className: 'controler-item controler-item-toogle item-checkbox', style: {
                            cursor: "pointer !important",
                            marginLeft: "20px",
                        }, onClick: () => toggleChatBan() },
                        React.createElement("span", { style: { fontWeight: '500' } }, t('menuController.privateMessage')),
                        React.createElement(Switch, { checked: !isBannedChat(participant, _state), id: "chat", onChange: () => toggleChatBan() })),
                    React.createElement("div", { "data-id": "1", className: 'controler-item controler-item-toogle item-checkbox', style: {
                            cursor: "pointer !important",
                            marginLeft: "20px",
                        }, onClick: () => toggleUploadBan() },
                        React.createElement("span", { style: { fontWeight: '500' } }, t('menuController.upload')),
                        React.createElement(Switch, { checked: !isBannedUpload(participant, _state), id: "upload", onChange: () => toggleUploadBan(), disabled: isBannedChat(participant, _state) })),
                    React.createElement("div", { "data-id": "1", className: 'controler-item controler-item-toogle item-checkbox', style: {
                            cursor: "pointer !important",
                            marginLeft: "20px",
                        }, onClick: () => toggleRaisehandBan() },
                        React.createElement("span", { style: { fontWeight: '500' } }, t('menuController.raisehand')),
                        React.createElement(Switch, { checked: !isBannedRaisehand(participant, _state), id: "raisehand", onChange: () => toggleRaisehandBan() })),
                    React.createElement("div", { "data-id": "1", className: 'controler-item controler-item-toogle item-checkbox', style: {
                            cursor: "pointer !important",
                            marginLeft: "20px",
                        }, onClick: () => togglePollBan() },
                        React.createElement("span", { style: { fontWeight: '500' } }, t('menuController.createPoll')),
                        React.createElement(Switch, { checked: !isBannedPoll(participant, _state), id: "poll", onChange: () => togglePollBan() })),
                    React.createElement("div", { "data-id": "1", className: 'controler-item controler-item-toogle item-checkbox', style: {
                            cursor: "pointer !important",
                            marginLeft: "20px",
                        }, onClick: () => toggleChangeNameBan() },
                        React.createElement("span", { style: { fontWeight: '500' } }, t('menuController.changeName')),
                        React.createElement(Switch, { checked: !isBannedChangeName(participant, _state), id: "changeName", onChange: () => toggleChangeNameBan() })),
                    React.createElement("div", { "data-id": "1", className: 'controler-item controler-item-toogle item-checkbox', style: {
                            cursor: "pointer !important",
                            marginLeft: "20px",
                        }, onClick: () => toggleRecordingBan() },
                        React.createElement("span", { style: { fontWeight: '500' } }, t('menuController.recording')),
                        React.createElement(Switch, { checked: !isBannedRecording(participant, _state), id: "recording", onChange: () => toggleRecordingBan() })))),
        breakoutRoomsButtons.length > 0 && (React.createElement(ContextMenuItemGroup, null,
            React.createElement("div", { className: styles.text }, t("breakoutRooms.actions.sendToBreakoutRoom")),
            breakoutRoomsButtons))));
};
export default ParticipantContextMenu;
